import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { maiuscula } from '../lib/caixa.ts';
import type { Caso } from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';

type Props = {
  caso: Caso;
  /** Preenchido quando se está corrigindo uma reunião já registrada. */
  inicial?: { id: string; data: string; assunto: string; observacao: string | null } | null;
  aoFechar: () => void;
  aoSalvar: (dados: { data: string; assunto: string; observacao?: string | null }) => Promise<void>;
};

export function RegistrarReuniao({ caso, inicial = null, aoFechar, aoSalvar }: Props) {
  const [data, setData] = useState(inicial?.data ?? '');
  const [assunto, setAssunto] = useState(inicial?.assunto ?? '');
  const [observacao, setObservacao] = useState(inicial?.observacao ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario({ data, assunto, observacao }, aoFechar);
  usarDialogo(guarda.fechar);

  const restantes = caso.reunioesRestantes;

  async function enviar(evento: FormEvent | MouseEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    if (!data) return setErro('Escolha a data da reunião.');
    if (!assunto.trim()) return setErro('Escreva o assunto tratado.');

    // A gravação só acontece depois do "sim". Ver lib/formulario.ts.
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({ data, assunto: assunto.trim(), observacao: observacao.trim() || null });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (a janela em blocos). */
  return (
    <>
      <JanelaEmBlocos
        rotulo="Registrar reunião"
        sobrelinha={
          <>
            Caso <span className="pap-codigo">{caso.codigo}</span> · {inicial ? 'corrigir reunião' : 'registrar reunião'}
          </>
        }
        titulo={inicial ? 'Corrigir reunião' : 'Registrar reunião'}
        sub={
          <>
            {caso.cliente.nome}
            {restantes !== null && (
              <span className={`etiqueta${restantes > 0 ? ' etiqueta-ativo' : ''}`}>
                {restantes > 0 ? `restam ${restantes} das ${caso.limites.reunioes} incluídas` : 'as reuniões incluídas já foram usadas'}
              </span>
            )}
          </>
        }
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={(e) => void enviar(e)}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : inicial ? 'Salvar' : 'Registrar'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Data" obrigatorio col={2} mono nota={data ? paraTela(data) : undefined}>
            <input id="rdata" type="date" value={data} onChange={(e) => setData(e.target.value)} autoFocus />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Assunto" obrigatorio col={4}>
            <input id="rassunto" value={assunto} onChange={(e) => setAssunto(maiuscula(e.target.value))} maxLength={200} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Observação" col={6}>
            <textarea id="robs" rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={2000} />
          </CampoDoPasso>
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="a reunião"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </>
  );
}
